import React from 'react';

import { NavLink } from 'react-router-dom';

import { useAuthState, useAuthActions } from '@/utils/auth';
import { useMakeFilteredUrl } from '@/hooks/useFilter';
import { UserQR } from '@/components/UserQR';
import { Button, ButtonProps } from '@/components/ui/button';
import { isManagedEmployeeEmail } from '@/utils/managedEmployeeEmail';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { AvatarImage, AvatarFallback, Avatar } from '@/components/ui/avatar';
import {
  User,
  QrCode,
  Settings,
  Info,
  HelpCircle,
  LogOut,
  MenuIcon,
} from 'lucide-react';
import { cn } from '@/utils/utils';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from '@/components/ui/sheet';
import { getAppReleaseShort } from '@/utils/appRelease';
import { HELP_CENTER_URL } from '@/utils/helpCenter';

type MenuItemProps = {
  children: React.ReactNode | React.ReactNode[];
  onClick?: () => void;
  asChild?: boolean;
};

const MenuItem = ({ children, onClick, asChild }: MenuItemProps) => {
  return (
    <DialogTrigger asChild>
      <Button
        variant="ghost"
        className="w-full justify-start gap-3 px-4 py-6 text-lg"
        asChild={asChild}
        onClick={onClick}
      >
        {children}
      </Button>
    </DialogTrigger>
  );
};

export type UserMenuProps = {
  size?: ButtonProps['size'];
  className?: string;
};

const UserMenu: React.FC<UserMenuProps> = ({ className }) => {
  const { user } = useAuthState();
  const make = useMakeFilteredUrl();
  const { signOut } = useAuthActions();
  const [infoModalOpen, setInfoModalOpen] = React.useState(false);
  const [QRModalOpen, setQRModalOpen] = React.useState(false);

  return (
    <Sheet>
      <Dialog
        open={infoModalOpen}
        onOpenChange={() => setInfoModalOpen((status) => !status)}
      >
        <DialogContent>
          Bilderit - Version {import.meta.env.VITE_APP_BRANCH}@
          {getAppReleaseShort()}
        </DialogContent>
      </Dialog>
      <Dialog
        open={QRModalOpen}
        onOpenChange={() => setQRModalOpen((status) => !status)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mi Código QR</DialogTitle>
          </DialogHeader>
          <UserQR />
        </DialogContent>
      </Dialog>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon" className={cn(className)}>
          <MenuIcon className="size-5" />
        </Button>
      </SheetTrigger>
      <SheetContent className="sm:max-w-md">
        <SheetHeader className="bg-sidebar p-6 -mx-6 -mt-6 rounded-t-lg">
          <div className="flex flex-col items-center gap-2">
            <Avatar className="size-20 border-4 ">
              <AvatarImage src="/avatar.png" alt="Usuario" />
              <AvatarFallback>
                {user
                  ? `${user.first_name} ${user.last_name}`
                      //maybe first and last name have more than one word each
                      .split(' ')
                      .map((name) => name.charAt(0))
                      .join('')
                  : 'User'}
              </AvatarFallback>
            </Avatar>
            <h2 className="text-xl font-bold text-foreground">
              {user ? `${user.first_name} ${user.last_name}` : '...'}
            </h2>
            <p className="text-sidebar-foreground">
              {user
                ? isManagedEmployeeEmail(user.email)
                  ? user.username
                  : user.email
                : '...'}
            </p>
          </div>
        </SheetHeader>
        <div className="flex flex-col -mx-6 -mb-6">
          <MenuItem asChild>
            <NavLink to={make('/profile')}>
              <User className="size-5" />
              <span>Perfil</span>
            </NavLink>
          </MenuItem>
          <MenuItem onClick={() => setQRModalOpen(true)}>
            <QrCode className="size-5" />
            <span>Mi Código QR</span>
          </MenuItem>
          <MenuItem asChild>
            <NavLink to={make('/settings')}>
              <Settings className="size-5" />
              <span>Configuración</span>
            </NavLink>
          </MenuItem>
          <MenuItem onClick={() => setInfoModalOpen(true)}>
            <Info className="size-5" />
            <span>Info</span>
          </MenuItem>
          <MenuItem asChild>
            <a href={HELP_CENTER_URL} target="_blank" rel="noopener noreferrer">
              <HelpCircle className="size-5" />
              <span>Centro de ayuda</span>
            </a>
          </MenuItem>
          <MenuItem onClick={() => signOut()}>
            <LogOut className="size-5" />
            <span>Salir</span>
          </MenuItem>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default UserMenu;
